import { useCallback, useMemo } from "react";
import { resolveConceptQueryTargetTypes } from "../services/autoTaggingService";
import {
  useAutoTaggingPlanTypes,
  type PlanTypesSource,
} from "./useAutoTaggingPlanTypes";
import { useConfigurableConcepts } from "./useConfigurableConcepts";

export type AssessmentPlanFilterHookOptions = {
  loadConcepts?: boolean;
  /** Tagging / Enhanced UIs only. O2O and Triage should omit this. */
  configurableOnly?: boolean;
  /** OTO uses `assessment-plans`; Tagging / Enhanced keep the default. */
  typesSource?: PlanTypesSource;
  objectSubType?: string | null;
};

function isFilterHookOptions(
  value: unknown,
): value is AssessmentPlanFilterHookOptions {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function useAssessmentPlanFilterOptions(
  targetType?: string | null,
  subTypeOrOptions?: string | null | AssessmentPlanFilterHookOptions,
) {
  const subType = isFilterHookOptions(subTypeOrOptions)
    ? subTypeOrOptions.objectSubType
    : subTypeOrOptions;
  const options = isFilterHookOptions(subTypeOrOptions)
    ? subTypeOrOptions
    : undefined;
  const loadConcepts = options?.loadConcepts !== false;

  const {
    planTypes,
    objectTypeOptions,
    defaultObjectType,
    getSubTypesForObjectType,
    loading: planTypesLoading,
  } = useAutoTaggingPlanTypes({
    configurableOnly: options?.configurableOnly === true,
    source: options?.typesSource ?? "auto-tagging",
  });

  const fallbackTypeCodes = useMemo(
    () => objectTypeOptions.map((item) => item.code).filter(Boolean),
    [objectTypeOptions],
  );

  // Empty Object Type (All) → load concepts for every available type.
  // Sub-Type only → map back to the parent `target_type`(s).
  const conceptTargetTypes = useMemo(() => {
    if (!loadConcepts) return [];
    return resolveConceptQueryTargetTypes({
      objectType: targetType,
      objectSubType: subType,
      planTypes,
      fallbackTypeCodes,
    });
  }, [fallbackTypeCodes, loadConcepts, planTypes, subType, targetType]);

  const { conceptOptions, loading: conceptsLoading } =
    useConfigurableConcepts(conceptTargetTypes, subType);

  const getObjectSubTypeOptions = useCallback(
    (objectType: string) => {
      if (!objectType) {
        const seen = new Set<string>();
        const options: Array<{ code: string; name: string }> = [];
        for (const planType of planTypes) {
          for (const item of planType.objectSubTypes) {
            if (seen.has(item.code)) continue;
            seen.add(item.code);
            options.push(item);
          }
        }
        return options;
      }
      return getSubTypesForObjectType(objectType);
    },
    [getSubTypesForObjectType, planTypes],
  );

  return {
    planTypes,
    objectTypeOptions,
    defaultObjectType,
    conceptOptions,
    getObjectSubTypeOptions,
    planTypesLoading,
    conceptsLoading,
  };
}
